import FontTypes from '~/constants/types/FontTypes';
import TextTypes from '~/constants/types/TextTypes';
import remoteServices from '~/constants/RemoteServices';
import {createAction} from 'redux-actions';
import uuid from 'uuid';
import {category as getCategory} from '~/actions/FontActions';
import Font from '~/data/records/Font';
import {
  canvasColors,
  colorsOnView,
  getCurrentDesignId,
  getIcons,
  getIconsArray,
  getLastIconColor,
  getLastTextIcon,
  getSelectedViewIcons
} from '~/selectors/CanvasSelectors';
import {
  getDecorationOverrideColor,
  getDefaultIconColor,
  getLaserEngravedDesignColor,
  getView,
  getViewConstraint,
  isAnchorLaserEngraved,
  isSelectedProductEmbroidered
} from '~/selectors/ProductSelectors';
import {topLeftCanvasCoordinates} from '~/data/accessors/canvas';
import {textRequestBuilder} from '~/utils/foundry';
import {roundDecimal} from '~/shared/utils/math';
import {
  FONT_MIN_HEIGHT_ERROR,
  FONT_OUTLINE_UNSUPPORTED,
  FONT_PIXEL_WIDTH_ERROR
} from '~/constants/NotificationConstants';
import {FONT_HEIGHT_BUFFER_SCALE} from '~/constants/FoundryConstants';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {
  getContrastColorFullDesign,
  setIconDecorationOverrideColors
} from '~/utils/icon';

export const addText = textIconData => (dispatch, getState) => {
  const state = getState();
  const decorationOverrideColor = getDecorationOverrideColor(state);

  const designId = getCurrentDesignId(state);
  const iconId = textIconData.iconId || uuid.v4();
  const lastIconColor = getLastIconColor(state);
  const lastTextIcon = getLastTextIcon(state);
  const isEmbroidered = isSelectedProductEmbroidered(state);
  const view = getView(state, {
    viewIdentifier: textIconData.viewId
  });
  const constraint = getViewConstraint(state)(view);
  const defaultFontData = {};

  if (isEmbroidered) {
    defaultFontData.font =
      DesignIconConstants.TEXT.DEFAULTS.EMBROIDERY_FONT_NAME;
    defaultFontData.fontCategory =
      DesignIconConstants.TEXT.DEFAULTS.EMBROIDERY_FONT_CATEGORY;
    defaultFontData.fontName =
      DesignIconConstants.TEXT.DEFAULTS.EMBROIDERY_FONT_DISPLAY;
  }
  if (lastTextIcon) {
    lastTextIcon.rendered = false;
  }

  const {x, y} = topLeftCanvasCoordinates(constraint);
  const icon = {
    ...defaultFontData,
    ...lastTextIcon,
    ...textIconData,
    color:
      decorationOverrideColor || lastIconColor || textIconData.defaultColor,
    id: iconId,
    x,
    y
  };

  if (icon.outlineColor) {
    icon.outlineColor = getContrastColorFullDesign(
      icon,
      getSelectedViewIcons(state).toArray(),
      colorsOnView(state),
      getIconsArray(state),
      canvasColors(state)
    );
  }

  if (decorationOverrideColor || isAnchorLaserEngraved(state)) {
    setIconDecorationOverrideColors({
      iconType: DesignIconConstants.TYPES.text,
      iconData: icon,
      decorationOverrideColor:
        decorationOverrideColor || getLaserEngravedDesignColor(state)
    });
  }

  dispatch({
    type: TextTypes.ADD_TEXT,
    payload: {
      designId,
      iconId,
      icon
    }
  });

  const font = new Font({
    displayName:
      icon.fontName ?? DesignIconConstants.TEXT.DEFAULTS.FONT_DISPLAY,
    name: icon.font ?? DesignIconConstants.TEXT.DEFAULTS.FONT_NAME
  });
  dispatch({
    type: FontTypes.UPDATE_FONT_SEARCH_HISTORY,
    payload: font
  });
};

export const updateEffect = createAction(TextTypes.UPDATE_EFFECT);
export const repositionForTextEffects = createAction(
  TextTypes.REPOSITION_FOR_TEXT_EFFECTS
);

export const checkFontPixelWidthErrorAsync =
  payload => (dispatch, getState) => {
    if (!payload.fontMinHeight) {
      return false;
    }

    const state = getState();
    const icon = getIcons(state)
      .find(i => i.id === payload.iconId)
      .toJS();
    const {printScaleFactor, constraint} = getView(state, {
      viewIdentifier: icon.viewId
    });
    icon.font = payload.font;
    icon.fontHeight = payload.fontMinHeight;
    return window
      .fetch(textRequestBuilder(icon, printScaleFactor, 'info'), {
        credentials: 'omit'
      })
      .then(res => res.json())
      .then(res => {
        return res.width > constraint.width * printScaleFactor;
      })
      .catch(err => {
        // same as verifyDimensions textAction error catch
        err.iconId = icon.id;
        dispatch(
          createAction(TextTypes.VERIFY_ERROR)({
            ...err,
            iconId: icon.id
          })
        );
      });
  };

export const updateFont = createAction(
  TextTypes.UPDATE_FONT,
  payload => payload,
  ({
    outlineError,
    fontMinHeight,
    iconFontHeight,
    fontPixelWidthError,
    fontName
  }) => {
    const fontHeightError = fontMinHeight > iconFontHeight;
    const confirmationRequired =
      outlineError || fontHeightError || fontPixelWidthError;
    let key = null;
    if (outlineError) {
      key = FONT_OUTLINE_UNSUPPORTED;
    } else if (fontPixelWidthError) {
      key = FONT_PIXEL_WIDTH_ERROR;
    } else if (fontHeightError) {
      key = FONT_MIN_HEIGHT_ERROR;
    }

    return {
      confirmationRequired,
      notification: {
        key,
        fontName
      }
    };
  }
);

export const scaleFontHeight =
  (selectedIcon, fontHeight, constraint) => dispatch => {
    const {
      id: iconId,
      height: iconHeight,
      type,
      width: iconWidth,
      fontHeight: oldFontHeight
    } = selectedIcon;
    const {height: constraintHeight, width: constraintWidth} = constraint;
    const fontHeightScale = fontHeight / oldFontHeight;
    const scaledWidth = fontHeightScale * iconWidth;
    const scaledHeight = fontHeightScale * iconHeight;
    if (scaledWidth > constraintWidth || scaledHeight > constraintHeight) {
      const maxWidthScale = constraintWidth / iconWidth;
      const maxHeightScale = constraintHeight / iconHeight;
      const maxScaleValue = Math.min(maxWidthScale, maxHeightScale);
      fontHeight = roundDecimal(
        maxScaleValue * oldFontHeight * FONT_HEIGHT_BUFFER_SCALE,
        2
      );
    }
    dispatch(updateFontHeight(iconId, fontHeight, type));
  };

export const updateFontHeight = createAction(
  TextTypes.UPDATE_FONT_HEIGHT,
  (iconId, fontHeight, iconType) => {
    return {iconId, fontHeight, iconType};
  }
);

export const updateOutline = payload => (dispatch, getState) => {
  const state = getState();
  const defaultIconColor = isAnchorLaserEngraved(state)
    ? getLaserEngravedDesignColor(state)
    : getDefaultIconColor(state);
  return dispatch({
    type: TextTypes.UPDATE_OUTLINE,
    payload: {...payload, defaultIconColor}
  });
};

export const updateText = createAction(TextTypes.UPDATE_TEXT);

export const updateFitToDimensions = createAction(
  TextTypes.UPDATE_FIT_TO_DIMENSIONS,
  (iconId, options) => {
    return {iconId, options};
  }
);

export const verifyDimensions = (icon, designIndex) => dispatch => {
  const action = {
    type: TextTypes.VERIFY_SUCCESS,
    payload: {
      iconId: icon.id,
      designIndex
    }
  };
  return dispatch(action);
};

/*
verifyDimensions and Font Height Rounding used to transform text in the lab.
Those are now removed, and in an effort to try to preserve design integrity,
we make an attempt to resize to what it was before.
see https://3.basecamp.com/4443489/projects/17685263
*/
export const guessSize =
  (icon, viewportScale, designIndex) => (dispatch, getState) => {
    const {printScaleFactor} = getView(getState(), {
      viewIdentifier: icon.viewId
    });
    const action = createAction(TextTypes.VERIFY_DIMENSIONS, iconId => ({
      iconId,
      designIndex
    }));
    dispatch(action(icon.id));
    return window
      .fetch(textRequestBuilder(icon, printScaleFactor, 'info'), {
        credentials: 'omit'
      })
      .then(res => res.json())
      .then(dimensions => {
        const {width} = dimensions;
        const guessScale = roundDecimal(width / printScaleFactor) / icon.width;
        const newFontHeight = guessScale * icon.fontHeight;
        const action = createAction(TextTypes.VERIFY_SUCCESS, () => ({
          fontHeight: newFontHeight,
          iconId: icon.id,
          designIndex
        }));
        dispatch(action(dimensions));
      })
      .catch(err => {
        // If something fails in either the request or subsequent logic we have no choice
        // but to accept the current and likely incorrect dimensions
        err.iconId = icon.id;
        dispatch(
          createAction(TextTypes.VERIFY_ERROR)({
            ...err,
            iconId: icon.id,
            designIndex
          })
        );
      });
  };

export const fetchFontCategory = selectedIcon => dispatch => {
  if (selectedIcon.fontCategory && selectedIcon.fontCategory.length > 0) {
    return;
  }
  return window
    .fetch(`${remoteServices.font_info}/${selectedIcon.font}.json`)
    .then(response => {
      if (response.ok) {
        response.json().then(data => {
          const font = new Font(data);
          const fontCategory = encodeURIComponent(font.category);
          dispatch({
            type: TextTypes.UPDATE_FONT_CATEGORY,
            payload: {
              iconId: selectedIcon.id,
              fontCategory,
              fontOutlineAllowed: font.supportsOutline
            }
          });

          getCategory({cat: fontCategory});
        });
      }
    });
};
